import React from 'react';
import { TableRow, TableCell, Chip, Typography, Box, Avatar, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { PatientBoardConfig, PatientCardVM, phaseStyle } from './types';
import { getScoreStyles } from './scoreStyles';

const CHIP_SX = { fontWeight: 600, fontSize: '0.68rem', height: 20 };

const PatientBoardRow: React.FC<{ patient: PatientCardVM; config: PatientBoardConfig }> = ({
  patient,
  config,
}) => {
  const navigate = useNavigate();
  const phStyle = phaseStyle(config, patient.phaseKey);
  const scoreStyle = getScoreStyles(patient.scoreCell.score);

  return (
    <TableRow
      hover
      onClick={() => navigate(`${config.routeBase}/patient/${patient.id}`)}
      sx={{
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
        borderLeft: '4px solid transparent',
        '&:hover': { backgroundColor: 'action.hover' },
        '& td': { py: 2 },
      }}
    >
      {/* Patient */}
      <TableCell>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3 }}>
            {patient.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
            {patient.mrn} • {patient.age} yrs
          </Typography>
        </Box>
      </TableCell>

      {/* Score / Disposition */}
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {patient.scoreCell.label}
          </Typography>
          <Chip label={patient.scoreCell.score} size="small" sx={{ fontWeight: 600, ...scoreStyle }} />
        </Box>
      </TableCell>

      {/* Location */}
      <TableCell>
        <Box>
          <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
            {patient.locationPrimary}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
            {patient.locationSecondary}
          </Typography>
        </Box>
      </TableCell>

      {/* Physician */}
      <TableCell>
        <Typography variant="body2" sx={{ lineHeight: 1.4, whiteSpace: 'nowrap' }}>
          {patient.physician.replace(/^Dr\.\s*/, '')}
        </Typography>
      </TableCell>

      {/* Diagnosis */}
      <TableCell>
        <Typography variant="body2" sx={{ lineHeight: 1.4 }}>{patient.diagnosis}</Typography>
      </TableCell>

      {/* Expected Discharge */}
      <TableCell>
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
          {patient.expectedDischarge}
        </Typography>
      </TableCell>

      {/* Current LOS */}
      <TableCell>
        <Typography variant="body2">{patient.currentLOS} days</Typography>
      </TableCell>

      {/* Phase */}
      <TableCell>
        <Chip
          label={phStyle.label}
          size="small"
          sx={{ ...CHIP_SX, bgcolor: phStyle.bgcolor, color: phStyle.color }}
        />
      </TableCell>

      {/* Urgency (optional) */}
      {config.showUrgency && (
        <TableCell>
          {patient.urgency && (
            <Chip
              label={patient.urgency.label}
              size="small"
              sx={{ ...CHIP_SX, bgcolor: patient.urgency.bgcolor, color: patient.urgency.color }}
            />
          )}
        </TableCell>
      )}

      {/* Assigned */}
      <TableCell align="center">
        {patient.assignedToName ? (
          <Tooltip title={patient.assignedToName} arrow>
            <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem', bgcolor: 'primary.main' }}>
              {patient.assignedToInitials}
            </Avatar>
          </Tooltip>
        ) : (
          <Tooltip title="Unassigned" arrow>
            <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem', bgcolor: 'grey.300', color: 'text.secondary' }}>
              —
            </Avatar>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
};

export default PatientBoardRow;
